import { toast } from "react-toastify";
import * as api from "../api";
import { branchAwareMessage } from "../utilities/vendorScope";
import {
  FETCH_OUTLETS_REQUEST,
  FETCH_OUTLETS_SUCCESS,
  FETCH_OUTLETS_FAILURE,
  CREATE_OUTLET,
  DELETE_OUTLET_SUCCESS,
  UPDATE_OUTLET_FAILURE,
  DELETE_OUTLET_FAILURE,
  UPDATE_OUTLET_SUCCESS,
  UPDATE_OUTLET_MENU_ITEMS_REQUEST,
  UPDATE_OUTLET_MENU_ITEMS_SUCCESS,
  UPDATE_OUTLET_MENU_ITEMS_FAILURE,
  TOGGLE_OUTLET_ACTIVATION_REQUEST,
  TOGGLE_OUTLET_ACTIVATION_SUCCESS,
  TOGGLE_OUTLET_ACTIVATION_FAILURE,
} from "../constants/actionTypes";

export const fetchOutlets = (restaurantId) => async (dispatch) => {
  dispatch({ type: FETCH_OUTLETS_REQUEST });
  try {
    const { data } = await api.apiFetchOutlets(restaurantId);
    dispatch({ type: FETCH_OUTLETS_SUCCESS, payload: data });
  } catch (error) {
    dispatch({ type: FETCH_OUTLETS_FAILURE, payload: error.message });
    toast.error("Failed to fetch outlets");
  }
};

/*
 * Outlet writes (4 Oct 2026). Each action is told its vertical, calls the one
 * endpoint for it, toasts once (the server's message on a refusal, which for
 * a branch member names the branches they may run) and rethrows, so a
 * caller can keep its dialog open or roll back. They used to guess: try the
 * pharmacy endpoint, fall back to the restaurant one on ANY error, so a 403
 * on one surfaced as a confusing 404 from the other, and every failure was
 * toasted twice (here and by the caller).
 *
 *   vertical  "restaurant" (Outlet) | "healthcare" (HealthcareOutlet)
 *   healthcareId  the pharmacy, for the healthcare create, update and delete
 */
const isHealthcare = (opts = {}, data = {}) =>
  opts.vertical === "healthcare" || (!opts.vertical && Boolean(data?.healthcareId));

const fail = (dispatch, type, error, fallback) => {
  if (type) dispatch({ type, payload: error?.message });
  toast.error(branchAwareMessage(error, fallback));
  throw error;
};

export const createOutlet = (outletData, opts = {}) => async (dispatch) => {
  try {
    const { data } = isHealthcare(opts, outletData)
      ? await api.apiCreateHealthcareOutlet(outletData.healthcareId || opts.healthcareId, outletData)
      : await api.apiCreateOutlet(outletData);
    dispatch({ type: CREATE_OUTLET, payload: data });
    toast.success("Outlet created");
    return data;
  } catch (error) {
    return fail(dispatch, null, error, "The outlet could not be created.");
  }
};

export const updateOutlet = (outletId, updatedData, opts = {}) => async (dispatch) => {
  try {
    const { data } = isHealthcare(opts, updatedData)
      ? await api.apiUpdateHealthcareOutlet(updatedData.healthcareId || opts.healthcareId, outletId, updatedData)
      : await api.apiUpdateOutlet(outletId, updatedData);
    dispatch({ type: UPDATE_OUTLET_SUCCESS, payload: data?.data || data });
    toast.success("Outlet updated");
    return data;
  } catch (error) {
    return fail(dispatch, UPDATE_OUTLET_FAILURE, error, "The outlet could not be updated.");
  }
};

export const deleteOutlet = (outletId, opts = {}) => async (dispatch) => {
  try {
    if (isHealthcare(opts)) await api.apiDeleteHealthcareOutlet(opts.healthcareId, outletId);
    else await api.apiDeleteOutlet(outletId);
    dispatch({ type: DELETE_OUTLET_SUCCESS, payload: outletId });
    toast.success("Outlet deleted");
    return outletId;
  } catch (error) {
    return fail(dispatch, DELETE_OUTLET_FAILURE, error, "The outlet could not be deleted.");
  }
};

export const updateOutletMenuItems = (outletId, menuItems, opts = {}) => async (dispatch) => {
  dispatch({ type: UPDATE_OUTLET_MENU_ITEMS_REQUEST });
  try {
    const { data } = isHealthcare(opts)
      ? await api.apiUpdateHealthcareOutletMenuItems(outletId, menuItems)
      : await api.apiUpdateOutletMenuItems(outletId, menuItems);
    dispatch({ type: UPDATE_OUTLET_MENU_ITEMS_SUCCESS, payload: data?.data || data });
    toast.success(isHealthcare(opts) ? "Products saved for this outlet" : "Menu items saved for this outlet");
    return data;
  } catch (error) {
    return fail(dispatch, UPDATE_OUTLET_MENU_ITEMS_FAILURE, error, "The outlet's items could not be saved.");
  }
};

export const toggleOutletActivation = (outletId, isActive, opts = {}) => async (dispatch) => {
  dispatch({ type: TOGGLE_OUTLET_ACTIVATION_REQUEST });
  try {
    const response = isHealthcare(opts)
      ? await api.apiToggleHealthcareOutletActivation(outletId, isActive)
      : await api.apiToggleOutletActivation(outletId, isActive);
    const payload = response?.data?.data || response?.data || response;
    dispatch({ type: TOGGLE_OUTLET_ACTIVATION_SUCCESS, payload });
    // The restaurant reducer keeps its own copy of each restaurant's outlets.
    dispatch({ type: "UPDATE_RESTAURANT_OUTLET", payload });
    toast.success(isActive ? "Outlet activated" : "Outlet deactivated");
    return response;
  } catch (error) {
    return fail(dispatch, TOGGLE_OUTLET_ACTIVATION_FAILURE, error, "The outlet could not be switched.");
  }
};
